import React from 'react';
import { IReactionDisposer, action, computed, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as d3 from 'd3';
import * as d3axis from 'd3-axis';
import * as d3scale from 'd3-scale';
import * as d3selection from 'd3-selection';
import * as d3shape from 'd3-shape';
import * as d3timeFormat from 'd3-time-format';
import classNames from 'classnames';
import { Host, HostThroughputData, IPangolinDomain, NetworkThroughputData, NetworkThroughputDuration } from 'services/pangolin/types';
import { Weekday, getMicroWeekday } from 'assets/enums/weekdays/weekdays';
import NetworkThroughputMessages from './network_throughput_messages';
import baseStyles from './network_throughput.module.css';
import { Theme, ThemeContext, ThemedStyles, useStyles } from 'ui/base/theme';
import { ThroughputService } from 'services/pangolin/throughput_service';
import { ServiceStatus } from 'services/base/types';
import { UnreachableFunction } from 'ui/base/types';


const SVG_WIDTH = 280;
const SVG_HEIGHT = SVG_WIDTH / 1.61803;

type NetworkThroughputStyles = {
  container: string;
  chart: string;
  axis: string;
  horizontalRule: string;
  ingressArea: string;
  ingressStroke: string;
  egressArea: string;
  egressStroke: string;
  loadingWidget: string;
  durationPicker: string;
  durationOption: string;
  activeDurationOption: string;
  legendContainer: string;
  legendItem: string;
  legendLabel: string;
  uploadLegendIcon: string;
  downloadLegendIcon: string;
};

const themedStyles: ThemedStyles<NetworkThroughputStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    chart: baseStyles.chart,
    axis: baseStyles.daylightAxis,
    horizontalRule: baseStyles.horizontalRule,
    ingressArea: baseStyles.daylightIngressArea,
    ingressStroke: baseStyles.daylightIngressStroke,
    egressArea: baseStyles.daylightEgressArea,
    egressStroke: baseStyles.daylightEgressStroke,
    loadingWidget: baseStyles.daylightLoadingWidget,
    durationPicker: baseStyles.durationPicker,
    durationOption: baseStyles.durationOption,
    activeDurationOption: baseStyles.activeDurationOption,
    legendContainer: baseStyles.legendContainer,
    legendItem: baseStyles.legendItem,
    legendLabel: baseStyles.daylightLegendLabel,
    uploadLegendIcon: baseStyles.uploadLegendIcon,
    downloadLegendIcon: baseStyles.downloadLegendIcon,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    chart: baseStyles.chart,
    axis: baseStyles.midnightAxis,
    horizontalRule: baseStyles.horizontalRule,
    ingressArea: baseStyles.midnightIngressArea,
    ingressStroke: baseStyles.midnightIngressStroke,
    egressArea: baseStyles.midnightEgressArea,
    egressStroke: baseStyles.midnightEgressStroke,
    loadingWidget: baseStyles.midnightLoadingWidget,
    durationPicker: baseStyles.durationPicker,
    durationOption: baseStyles.durationOption,
    activeDurationOption: baseStyles.activeDurationOption,
    legendContainer: baseStyles.legendContainer,
    legendItem: baseStyles.legendItem,
    legendLabel: baseStyles.midnightLegendLabel,
    uploadLegendIcon: baseStyles.uploadLegendIcon,
    downloadLegendIcon: baseStyles.downloadLegendIcon,
  },
};

function round(value: number, places: number) {
  return Math.floor(value * Math.pow(10, places)) / Math.pow(10, places);
}

function d3ToDate(value: number | Date | { valueOf(): number }): Date {
  // Always creates a new date
  let dvalue: Date;
  if (!isNaN(+value)) {
    dvalue = new Date(+value);
  } else if (value instanceof Date) {
    dvalue = new Date(value.getTime());
  } else {
    dvalue = new Date(value.valueOf());
  }
  return dvalue;
}

function dateToWeekday(start: Date) {
  return (_: unknown, index: number) => {
    const startDay = start.getDay();
    const day = (startDay + index + 1) % 7;

    switch(day) {
      case 0:
        return getMicroWeekday(Weekday.SUNDAY);
      case 1:
        return getMicroWeekday(Weekday.MONDAY);
      case 2:
        return getMicroWeekday(Weekday.TUESDAY);
      case 3:
        return getMicroWeekday(Weekday.WEDNESDAY);
      case 4:
        return getMicroWeekday(Weekday.THURSDAY);
      case 5:
        return getMicroWeekday(Weekday.FRIDAY);
      case 6:
        return getMicroWeekday(Weekday.SATURDAY);
      default:
        return getMicroWeekday(Weekday.SUNDAY);
    }
  };
}

function dateToRoundedMinutes(rounding: number) {
  const dateFormatter = d3timeFormat.timeFormat('%H:%M');

  return (value: number | Date | { valueOf(): number }) => {
    const date = d3ToDate(value);
    const initialMinutes = date.getMinutes();
    const initialHours = date.getHours();

    const roundedMinutes = Math.round(initialMinutes / rounding) * rounding;
    let finalMinutes, finalHours;
    if (roundedMinutes >= 60) {
      finalMinutes = roundedMinutes % 60;
      finalHours = (initialHours + 1) % 24;
    } else {
      finalMinutes = roundedMinutes;
      finalHours = initialHours;
    }

    date.setSeconds(0);
    date.setMinutes(finalMinutes);
    date.setHours(finalHours);
    return dateFormatter(date);
  };
}

type NormalizedThroughput = {
  start: Date;
  end: Date;
  upload: number;
  download: number;
};

function normalizeNetworkThroughputData(data: NetworkThroughputData): NormalizedThroughput {
  return {
    start: data.start,
    end: data.end,
    upload: data.egressBytes,
    download: data.ingressBytes,
  };
}

function normalizeHostThroughputData(data: HostThroughputData): NormalizedThroughput {
  return {
    start: data.start,
    end: data.end,
    upload: data.externalTxBytes,
    download: data.externalRxBytes,
  };
}

type NetworkThroughputChartProps = {
  kind: 'network';
  data: NetworkThroughputData[];
  duration: NetworkThroughputDuration;
};
type HostThroughputChartProps = {
  kind: 'host';
  data: HostThroughputData[];
  duration: NetworkThroughputDuration;
};
type ThroughputChartProps = NetworkThroughputChartProps | HostThroughputChartProps;

@observer
class ThroughputChart extends React.Component<ThroughputChartProps> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  @observable.ref
  chart?: HTMLElement;

  @action.bound
  setChart(chart: HTMLDivElement) {
    this.chart = chart;
    this.renderChart();
  }

  componentDidUpdate() {
    this.renderChart();
  }

  componentDidMount() {
    this.renderChart();
  }

  @computed get normalizedData(): NormalizedThroughput[] {
    if (this.props.kind === 'network') {
      return this.props.data.map(normalizeNetworkThroughputData);
    } else {
      return this.props.data.map(normalizeHostThroughputData);
    }
  }

  yAxisRenderer(value: number | Date | { valueOf(): number }) {
    // Renderer for y axis tick labels
    let nvalue: number;
    if (!isNaN(+value)) {
      nvalue = +value;
    } else if (value instanceof Date) {
      nvalue = value.getTime();
    } else {
      nvalue = value.valueOf();
    }
    if (nvalue < 0.000001) {
      return '';
    } else if (Math.abs(nvalue) >= 1000000000000) {
      return `${round(nvalue / 1000000000000, 2)} ${NetworkThroughputMessages.tb()}`;
    } else if (Math.abs(nvalue) >= 1000000000) {
      return `${round(nvalue / 1000000000, 2)} ${NetworkThroughputMessages.gb()}`;
    } else if (Math.abs(nvalue) >= 1000000) {
      return `${round(nvalue / 1000000, 2)} ${NetworkThroughputMessages.mb()}`;
    } else {
      return `${round(nvalue / 1000, 2)} ${NetworkThroughputMessages.kb()}`;
    }
  }

  renderChart() {
    if (this.chart == null) {
      return;
    }
    const data = this.normalizedData;
    const styles = themedStyles[this.context];

    d3selection.select(this.chart).selectAll('svg').remove();
    const svg = d3selection.select(this.chart).append('svg');
    svg.classed(baseStyles.chart, true);
    svg.attr('preserveAspectRatio', 'xMinYMin meet');
    svg.attr('viewBox', `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`);

    // set the dimensions and margins of the graph
    const margin = { top: 16, right: 4, bottom: 16, left: 56 };
    const width = SVG_WIDTH - margin.left - margin.right;
    const height = SVG_HEIGHT - margin.top - margin.bottom;

    // Compute the highest value of the xDomain and create a scaling function
    const xDomain = data.map((point) => point.start.getTime()).concat(data.map((point) => point.end.getTime()));
    const xMin = Math.min(...xDomain);
    const xMax = Math.max(...xDomain);
    const xScale = d3scale.scaleTime().domain([xMin, xMax]).range([0, width]);

    // Compute the highest value of the yDomain and create a scaling function
    const yDomain: number[] = [];
    yDomain.push(...data.map((point) => point.download));
    yDomain.push(...data.map((point) => point.upload));
    const yMin = 0;
    const yDomainMax = Math.max(...yDomain);
    // Have a minimum of at least 56kbps on the chart, otherwise default to 10mbps
    const yMax = yDomainMax >= 56000 ? yDomainMax : 10000000;
    const yScale = d3scale.scaleLinear().domain([yMin, yMax]).range([height, 0]);

    // Create the x axis
    const xAxis = svg.append('g');

    let xAxisTicks: Date[];
    let xAxisRenderer: (value: any, index: number) => string; /* eslint-disable-line @typescript-eslint/no-explicit-any */

    switch(this.props.duration) {
      case NetworkThroughputDuration.WEEKLY:
        xAxisTicks = d3.range(1/14, 1, 1/7).map((x) => xScale.invert(x * width));
        xAxisRenderer = dateToWeekday(d3ToDate(xMax));
        break;
      case NetworkThroughputDuration.DAILY:
        xAxisTicks = d3.range(1/8, 1, 1/4).map((x) => xScale.invert(x * width));
        xAxisRenderer = dateToRoundedMinutes(15);
        break;
      case NetworkThroughputDuration.HOURLY:
        xAxisTicks = d3.range(1/8, 1, 1/4).map((x) => xScale.invert(x * width));
        xAxisRenderer = dateToRoundedMinutes(5);
        break;
      default:
        throw UnreachableFunction(this.props.duration);
    }

    xAxis.attr('transform', `translate(${margin.left},${margin.top + height})`)
      .classed(styles.axis, true)
      .call(d3axis.axisBottom(xScale)
        .tickSize(0)
        .tickValues(xAxisTicks)
        .tickFormat(xAxisRenderer))
      .call(g => g.select('.domain').remove());

    // Create the y axis
    const yAxis = svg.append('g');
    yAxis.attr('transform', `translate(${margin.left},${margin.top})`)
      .classed(styles.axis, true)
      .call(d3axis.axisLeft(yScale)
        .tickSize(0)
        .ticks(5)
        .tickFormat(this.yAxisRenderer))
      .call(g => g.select('.domain').remove());

    // Create the dotted lines
    const yAxisRule = svg.append('g');
    yAxisRule.attr('transform', `translate(${margin.left},${margin.top})`)
      .classed(styles.horizontalRule, true)
      .call(d3axis.axisLeft(yScale)
        .ticks(5)
        .tickSizeInner(-width)
        .tickSizeOuter(0)
        .tickFormat(() => ''))
      .call(g => g.select('.domain').remove())
      .call(g => g.select('.tick:first-child').remove());

    const chart = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create the egress fill
    chart.append('path')
      .datum(data.map((d) => [d.end, d.upload]))
      .classed(styles.egressArea, true)
      .attr('d', d3shape.area()
        .curve(d3shape.curveMonotoneX)
        .x(([end, _]) => xScale(end))
        .y0(yScale(0))
        .y1(([_, bytes]) => yScale(bytes)) as any); /* eslint-disable-line @typescript-eslint/no-explicit-any */

    // Create the egress stroke
    chart.append('path')
      .datum(data.map((d) => [d.end, d.upload]))
      .classed(styles.egressStroke, true)
      .attr('d', d3shape.area()
        .curve(d3shape.curveMonotoneX)
        .x(([end, _]) => xScale(end))
        .y(([_, bytes]) => yScale(bytes)) as any); /* eslint-disable-line @typescript-eslint/no-explicit-any */

    // Create the ingress fill
    chart.append('path')
      .datum(data.map((d) => [d.end, d.download]))
      .classed(styles.ingressArea, true)
      .attr('d', d3shape.area()
        .curve(d3shape.curveMonotoneX)
        .x(([end, _]) => xScale(end))
        .y0(yScale(0))
        .y1(([_, bytes]) => yScale(bytes)) as any); /* eslint-disable-line @typescript-eslint/no-explicit-any */

    // Create the ingress stroke
    chart.append('path')
      .datum(data.map((d) => [d.end, d.download]))
      .classed(styles.ingressStroke, true)
      .attr('d', d3shape.area()
        .curve(d3shape.curveMonotoneX)
        .x(([end, _]) => xScale(end))
        .y(([_, bytes]) => yScale(bytes)) as any); /* eslint-disable-line @typescript-eslint/no-explicit-any */
  }

  render() {
    const styles = themedStyles[this.context];

    // Stuff to go later
    return (
      <div ref={this.setChart} className={styles.container}>
      </div>
    );
  }
}

const LoadingWidget = () => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.loadingWidget}/>
  );
};

const ThroughputLegend = () => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.legendContainer}>
      <div className={styles.legendItem}>
        <div className={styles.uploadLegendIcon}/>
        <span className={styles.legendLabel}>
          {NetworkThroughputMessages.uploadLegendLabel()}
        </span>
      </div>
      <div className={styles.legendItem}>
        <div className={styles.downloadLegendIcon}/>
        <span className={styles.legendLabel}>
          {NetworkThroughputMessages.downloadLegendLabel()}
        </span>
      </div>
    </div>
  );
};

type DurationPickerProps = {
  duration: NetworkThroughputDuration;
  setWeekly(): void;
  setDaily(): void;
  setHourly(): void;
};
const DurationPicker = ({ duration, setWeekly, setDaily, setHourly }: DurationPickerProps) => {
  const styles = useStyles(themedStyles);
  const isHourly = duration === NetworkThroughputDuration.HOURLY;
  const isDaily = duration === NetworkThroughputDuration.DAILY;
  const isWeekly = duration === NetworkThroughputDuration.WEEKLY;
  return (
    <div className={styles.durationPicker}>
      <div className={classNames(styles.durationOption, { [styles.activeDurationOption]: isHourly })} onClick={!isHourly ? setHourly : undefined}>
        {NetworkThroughputMessages.hourly()}
      </div>
      <div className={classNames(styles.durationOption, { [styles.activeDurationOption]: isDaily })} onClick={!isDaily ? setDaily : undefined}>
        {NetworkThroughputMessages.daily()}
      </div>
      <div className={classNames(styles.durationOption, { [styles.activeDurationOption]: isWeekly })} onClick={!isWeekly ? setWeekly : undefined}>
        {NetworkThroughputMessages.weekly()}
      </div>
    </div>
  );
};

type PangolinNetworkThroughputProps = {
  pangolin: IPangolinDomain;
};
@observer
export class PangolinNetworkThroughput extends React.Component<PangolinNetworkThroughputProps> {
  @observable.ref
  duration: NetworkThroughputDuration;

  disposer?: IReactionDisposer;

  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  constructor(props: PangolinNetworkThroughputProps) {
    super(props);
    this.duration = NetworkThroughputDuration.HOURLY;
  }

  componentDidMount() {
    if (this.disposer == null) {
      this.disposer = reaction(
        () => this.props.pangolin.throughput[this.duration],
        this.fetchThroughputData,
        { fireImmediately: true },
      );
    }
  }

  componentWillUnmount() {
    if (this.disposer != null) {
      this.disposer();
      this.disposer = undefined;
    }
  }

  @action.bound
  setDurationToHourly() {
    this.duration = NetworkThroughputDuration.HOURLY;
  }

  @action.bound
  setDurationToDaily() {
    this.duration = NetworkThroughputDuration.DAILY;
  }

  @action.bound
  setDurationToWeekly() {
    this.duration = NetworkThroughputDuration.WEEKLY;
  }

  @action.bound
  fetchThroughputData() {
    const enabled = this.props.pangolin.throughputEnabled;
    if (enabled === ServiceStatus.FAILED) {
      return;
    }
    const cached = this.props.pangolin.throughput[this.duration];
    if (cached.status === ServiceStatus.FRESH) {
      ThroughputService.fetchPangolinReport(this.props.pangolin, this.duration);
    }
  }

  render() {
    const enabled = this.props.pangolin.throughputEnabled;
    // Check if throughput is supported by the pangolin version
    if (enabled !== ServiceStatus.LOADED) {
      return null;
    }

    const cached = this.props.pangolin.throughput[this.duration];
    if (cached.status === ServiceStatus.LOADED) {
      return (
        <>
          <DurationPicker
            duration={this.duration}
            setHourly={this.setDurationToHourly}
            setDaily={this.setDurationToDaily}
            setWeekly={this.setDurationToWeekly}
          />
          <ThroughputChart kind='network' data={cached.data} duration={this.duration}/>
          <ThroughputLegend/>
        </>
      );
    } else if (cached.status === ServiceStatus.LOADING) {
      return (
        <>
          <DurationPicker
            duration={this.duration}
            setHourly={this.setDurationToHourly}
            setDaily={this.setDurationToDaily}
            setWeekly={this.setDurationToWeekly}
          />
          <LoadingWidget/>
        </>
      );
    }
    return null;
  }
}

type HostNetworkThroughputProps = {
  pangolin: IPangolinDomain;
  host: Host;
};
@observer
export class HostNetworkThroughput extends React.Component<HostNetworkThroughputProps> {
  @observable.ref
  duration: NetworkThroughputDuration;

  disposer?: IReactionDisposer;
  constructor(props: HostNetworkThroughputProps) {
    super(props);
    this.duration = NetworkThroughputDuration.HOURLY;
  }

  componentDidMount() {
    if (this.disposer == null) {
      this.disposer = reaction(
        () => this.props.host.throughput[this.duration],
        this.fetchThroughputData,
        { fireImmediately: true },
      );
    }
  }

  componentWillUnmount() {
    if (this.disposer != null) {
      this.disposer();
      this.disposer = undefined;
    }
  }

  @action.bound
  setDurationToHourly() {
    this.duration = NetworkThroughputDuration.HOURLY;
  }

  @action.bound
  setDurationToDaily() {
    this.duration = NetworkThroughputDuration.DAILY;
  }

  @action.bound
  setDurationToWeekly() {
    this.duration = NetworkThroughputDuration.WEEKLY;
  }

  @action.bound
  fetchThroughputData() {
    const enabled = this.props.pangolin.throughputEnabled;
    if (enabled === ServiceStatus.FAILED) {
      return;
    }
    const cached = this.props.host.throughput[this.duration];
    if (cached.status === ServiceStatus.FRESH) {
      ThroughputService.fetchHostReport(this.props.pangolin, this.props.host, this.duration);
    }
  }

  render() {
    const enabled = this.props.pangolin.throughputEnabled;
    if (enabled !== ServiceStatus.LOADED) {
      return null;
    }

    const cached = this.props.host.throughput[this.duration];
    if (cached.status === ServiceStatus.LOADED) {
      return (
        <>
          <DurationPicker
            duration={this.duration}
            setHourly={this.setDurationToHourly}
            setDaily={this.setDurationToDaily}
            setWeekly={this.setDurationToWeekly}
          />
          <ThroughputChart kind='host' data={cached.data} duration={this.duration}/>
        </>
      );
    } else if (cached.status === ServiceStatus.LOADING) {
      return (
        <>
          <DurationPicker
            duration={this.duration}
            setHourly={this.setDurationToHourly}
            setDaily={this.setDurationToDaily}
            setWeekly={this.setDurationToWeekly}
          />
          <LoadingWidget/>
        </>
      );
    }
    return null;
  }
}
